import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { C } from '../design-system/primitives/constants';
import { safeMd } from '../../lib/safeMd';

const TREND_COLORS = {
  up: C.elite,
  down: C.dev,
  neutral: C.gold,
};

const TREND_ICONS = {
  up: TrendingUp,
  down: TrendingDown,
  neutral: Minus,
};

// --- Sub-components ---

function MetricBadge({ label, value, trend = 'neutral', delta }) {
  const color = TREND_COLORS[trend] || TREND_COLORS.neutral;
  const Icon = TREND_ICONS[trend] || Minus;
  const ariaLabel = `${label}: ${value}${trend !== 'neutral' ? `, trending ${trend}` : ''}${delta ? ` by ${delta}` : ''}`;

  return (
    <span
      data-testid="metric-badge"
      data-trend={trend}
      aria-label={ariaLabel}
      role="status"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 10px',
        borderRadius: 999,
        background: C.s3,
        border: `1px solid ${color}40`,
        marginRight: 6,
        marginBottom: 4,
      }}
    >
      <span style={{ fontSize: 11, color: C.t2 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color }}>{value}</span>
      <Icon size={12} color={color} aria-hidden="true" />
      {delta && <span style={{ fontSize: 11, color }}>{delta}</span>}
    </span>
  );
}

function InlineTable({ headers, rows }) {
  const [expanded, setExpanded] = useState(false);
  const visibleRows = expanded ? rows : rows.slice(0, 5);

  return (
    <div style={{ overflowX: 'auto', marginTop: 6, marginBottom: 6 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                style={{
                  textAlign: 'left',
                  padding: '4px 8px',
                  borderBottom: `1px solid ${C.border}`,
                  color: C.t2,
                  fontWeight: 600,
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  style={{
                    padding: '3px 8px',
                    borderBottom: `1px solid ${C.border}`,
                    color: C.text,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 5 && (
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            marginTop: 4,
            fontSize: 11,
            color: C.gold,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px 0',
          }}
        >
          {expanded ? 'Show less' : `Show all (${rows.length})`}
        </button>
      )}
    </div>
  );
}

function InsightCard({ severity, text, confidence }) {
  const borderColor = TREND_COLORS[severity === 'positive' ? 'up' : severity === 'negative' ? 'down' : 'neutral'];
  const dimmed = confidence != null && confidence < 0.7;

  return (
    <div
      style={{
        borderLeft: `3px solid ${borderColor}`,
        background: C.s2,
        padding: '8px 12px',
        marginTop: 6,
        marginBottom: 6,
        borderRadius: '0 6px 6px 0',
        opacity: dimmed ? 0.6 : 1,
      }}
    >
      <span style={{ fontSize: 13, color: C.text }}>{text}</span>
      {confidence != null && (
        <span style={{ fontSize: 11, color: C.t2, marginLeft: 8 }}>
          {Math.round(confidence * 100)}%
        </span>
      )}
    </div>
  );
}

/**
 * TextSection renders markdown content via safeMd (DOMPurify-sanitized).
 * This mirrors the existing pattern in MessageBubble.jsx and CoachingCanvas.jsx.
 */
function TextSection({ content }) {
  return (
    <div
      style={{ marginTop: 4, marginBottom: 4 }}
      dangerouslySetInnerHTML={{ __html: safeMd(content) }}
    />
  );
}

// --- Main component ---

export default function RichAnalyticsBubble({ sections }) {
  if (!Array.isArray(sections) || sections.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {sections.map((section, i) => {
        switch (section.type) {
          case 'metric':
            return <MetricBadge key={i} {...section} />;
          case 'table':
            return <InlineTable key={i} {...section} />;
          case 'insight':
            return <InsightCard key={i} {...section} />;
          case 'text':
          default:
            return <TextSection key={i} content={section.content} />;
        }
      })}
    </div>
  );
}

export { MetricBadge, InlineTable, InsightCard, TextSection };
